import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ScenarioFormData, ValidationResult, GameScenarioContent } from '../../types/ScenarioManagement';
import { useTheme } from '../../contexts/ThemeContext';
import { ExecutiveSummaryEditor } from './ExecutiveSummaryEditor';
import { InvestmentTab } from '../InvestmentManagement/InvestmentTab';
import { IncidentTab } from '../IncidentManagement/IncidentTab';
import SynergyEditor from './SynergyEditor';
import { StandardButton, FormField, SelectField, TextAreaField } from '../ui';

interface SimpleScenarioFormProps {
  initialData?: Partial<ScenarioFormData>;
  onSubmit: (data: ScenarioFormData) => Promise<void>;
  onCancel: () => void;
  mode: 'create' | 'edit';
}

const SimpleScenarioForm: React.FC<SimpleScenarioFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  mode
}) => {
  const { isDark } = useTheme();
  
  const [formData, setFormData] = useState<Partial<ScenarioFormData>>(() => {
    // First spread the initial data
    const baseData = {
      ...initialData,
      // Then override with specific field defaults
      title: initialData?.title || '',
      subtitle: initialData?.subtitle || '',
      description: initialData?.description || '',
      icon: initialData?.icon || '🎯',
      difficulty: initialData?.difficulty || 'Intermediate',
      isAvailable: initialData?.isAvailable !== undefined ? initialData.isAvailable : true,
    };
    
    // Ensure content is properly preserved with all fields including executiveSummary
    const defaultContent = {
      objectives: [],
      successCriteria: [],
      availableResources: [],
      constraints: [],
      executiveSummary: ''
    };
    
    baseData.content = initialData?.content ? {
      ...defaultContent,
      ...initialData.content
    } : defaultContent;
    
    return baseData;
  });

  const [validation, setValidation] = useState<ValidationResult>({ isValid: true, errors: {} });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<'basic' | 'briefing' | 'investments' | 'incidents' | 'synergies'>('basic');

  const validateForm = useCallback((): ValidationResult => {
    const errors: Record<string, string[]> = {};

    if (!formData.title?.trim()) {
      errors.title = ['Title is required'];
    } else if (formData.title.length < 3) {
      errors.title = ['Title must be at least 3 characters'];
    }

    if (!formData.subtitle?.trim()) {
      errors.subtitle = ['Subtitle is required'];
    }

    if (!formData.description?.trim()) {
      errors.description = ['Description is required'];
    } else if (formData.description.length < 20) {
      errors.description = ['Description must be at least 20 characters'];
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }, [formData]);

  const handleFieldChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationResult = validateForm();
    setValidation(validationResult);

    if (!validationResult.isValid) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData as ScenarioFormData);
    } catch (error) {
      console.error('Failed to submit form:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to save scenario. Please try again.';
      setValidation({
        isValid: false,
        errors: { submit: [errorMessage] }
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const ValidationErrors = ({ errors }: { errors?: string[] }) => {
    if (!errors || errors.length === 0) return null;
    return (
      <div className="mt-1">
        {errors.map((error, index) => (
          <p key={index} className="text-sm text-red-400">{error}</p>
        ))}
      </div>
    );
  };

  const themeClasses = {
    container: isDark 
      ? 'bg-white/[0.03] border-white/10' 
      : 'bg-white border-gray-200',
    header: isDark 
      ? 'border-white/10' 
      : 'border-gray-200',
    title: isDark 
      ? 'text-white' 
      : 'text-gray-900',
    subtitle: isDark 
      ? 'text-slate-300' 
      : 'text-gray-600',
    label: isDark 
      ? 'text-slate-300' 
      : 'text-gray-700',
    input: isDark 
      ? 'bg-white/[0.05] border-white/10 text-white placeholder-slate-500 focus:border-professional-blue-500' 
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-professional-blue-500',
    iconButton: (selected: boolean) => selected
      ? 'bg-professional-blue-600 shadow-lg'
      : isDark 
        ? 'bg-white/[0.05] hover:bg-white/[0.1]' 
        : 'bg-gray-100 hover:bg-gray-200'
  };

  return (
    <div className="w-full">
      <form onSubmit={handleSubmit}>
        <div className={`${themeClasses.container} rounded-2xl backdrop-blur-md shadow-xl`}>
          {/* Header */}
          <div className={`p-6 border-b ${themeClasses.header}`}>
            <h2 className={`text-2xl font-semibold ${themeClasses.title} mb-2`}>
              {mode === 'create' ? 'Create New Scenario' : 'Edit Scenario'}
            </h2>
            <p className={themeClasses.subtitle}>
              {activeTab === 'basic' 
                ? 'Update the basic information displayed on the scenario selection screen'
                : activeTab === 'briefing'
                ? 'Edit the executive briefing content shown to players at the start'
                : activeTab === 'investments'
                ? 'Configure available cybersecurity investments for this scenario'
                : activeTab === 'incidents'
                ? 'Manage cybersecurity incidents and response options'
                : 'Define investment synergies and response bonuses'
              }
            </p>
          </div>

          {/* Tabs - Always show Executive Briefing */}
          <div className={`flex border-b ${themeClasses.header}`}>
              <button
                type="button"
                onClick={() => setActiveTab('basic')}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === 'basic'
                    ? 'border-b-2 border-professional-blue-500 text-professional-blue-600'
                    : isDark 
                      ? 'text-slate-400 hover:text-white' 
                      : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                📝 Basic Information
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('briefing')}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === 'briefing'
                    ? 'border-b-2 border-professional-blue-500 text-professional-blue-600'
                    : isDark 
                      ? 'text-slate-400 hover:text-white' 
                      : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                📊 Executive Briefing
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('investments')}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === 'investments'
                    ? 'border-b-2 border-professional-blue-500 text-professional-blue-600'
                    : isDark 
                      ? 'text-slate-400 hover:text-white' 
                      : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                💰 Investments
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('incidents')}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === 'incidents'
                    ? 'border-b-2 border-professional-blue-500 text-professional-blue-600'
                    : isDark 
                      ? 'text-slate-400 hover:text-white' 
                      : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                🚨 Incidents
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('synergies')}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === 'synergies'
                    ? 'border-b-2 border-professional-blue-500 text-professional-blue-600'
                    : isDark 
                      ? 'text-slate-400 hover:text-white' 
                      : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                🤝 Synergies
              </button>
            </div>

          {/* Tab Content */}
          <AnimatePresence mode="wait">
            {activeTab === 'basic' && (
              <motion.div
                key="basic"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
                className="p-6 space-y-6"
              >
            {/* Icon Picker */}
            <div>
              <label className={`block text-sm font-medium ${themeClasses.label} mb-2`}>
                Scenario Icon
              </label>
              <div className="flex gap-2 flex-wrap">
                {['🏢', '🤝', '🤖', '🚀', '🔒', '🌍', '💼', '🎯', '⚡', '🛡️', '📊', '🔧'].map(icon => (
                  <button
                    key={icon}
                    type="button"
                    onClick={() => handleFieldChange('icon', icon)}
                    className={`text-2xl p-3 rounded-lg transition-all ${
                      themeClasses.iconButton(formData.icon === icon)
                    }`}
                  >
                    {icon}
                  </button>
                ))}
              </div>
            </div>

            {/* Title */}
            <FormField
              label="Scenario Title"
              value={formData.title || ''}
              onChange={e => handleFieldChange('title', e.target.value)}
              placeholder="e.g., TechCorp International"
              required
              error={validation.errors.title?.[0]}
            />

            {/* Subtitle */}
            <FormField
              label="Subtitle"
              value={formData.subtitle || ''}
              onChange={e => handleFieldChange('subtitle', e.target.value)}
              placeholder="e.g., Geographic Expansion & AI Adoption"
              required
              error={validation.errors.subtitle?.[0]}
            />

            {/* Description */}
            <TextAreaField
              label="Description"
              value={formData.description || ''}
              onChange={e => handleFieldChange('description', e.target.value)}
              rows={4}
              placeholder="Provide a compelling description of the scenario..."
              required
              error={validation.errors.description?.[0]}
            />

            {/* Difficulty */}
            <SelectField
              label="Difficulty Level"
              value={formData.difficulty || 'Intermediate'}
              onChange={e => handleFieldChange('difficulty', e.target.value)}
              options={[
                { value: 'Beginner', label: 'Beginner' },
                { value: 'Intermediate', label: 'Intermediate' },
                { value: 'Advanced', label: 'Advanced' },
                { value: 'Expert', label: 'Expert' },
                { value: 'Tech', label: 'Tech' },
                { value: 'Exec', label: 'Exec' }
              ]}
              required
            />

            {/* Availability */}
            <div>
              <label className={`block text-sm font-medium ${themeClasses.label} mb-2`}>
                Availability
              </label>
              <div className="flex items-center gap-6">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="availability"
                    checked={formData.isAvailable === true}
                    onChange={() => handleFieldChange('isAvailable', true)}
                    className="w-3 h-3 mr-2 text-professional-blue-600"
                  />
                  <span className={themeClasses.title}>Available</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="availability"
                    checked={formData.isAvailable === false}
                    onChange={() => handleFieldChange('isAvailable', false)}
                    className="w-3 h-3 mr-2"
                  />
                  <span className={themeClasses.title}>Coming Soon</span>
                </label>
              </div>
            </div>
              </motion.div>
            )}

            {activeTab === 'briefing' && (
              <motion.div
                key="briefing"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
                className="p-6"
              >
                <ExecutiveSummaryEditor
                  summary={(formData.content as GameScenarioContent)?.executiveSummary || ''}
                  onChange={(updatedSummary) => {
                    const gameContent = formData.content as GameScenarioContent || {
                      objectives: [],
                      successCriteria: [],
                      availableResources: [],
                      constraints: [],
                      executiveSummary: ''
                    };
                    const updatedContent = {
                      ...gameContent,
                      executiveSummary: updatedSummary
                    };
                    handleFieldChange('content', updatedContent);
                  }}
                />
              </motion.div>
            )}

            {activeTab === 'investments' && (
              <motion.div
                key="investments"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
                className="p-6"
              >
                <InvestmentTab 
                  investments={(formData.content as GameScenarioContent)?.investments || []}
                  selectedInvestmentIds={(formData.content as GameScenarioContent)?.selectedInvestmentIds || []}
                  onChange={(updatedInvestments) => {
                    setFormData(prev => {
                      const gameContent = (prev.content as GameScenarioContent) || {
                        objectives: [],
                        successCriteria: [],
                        availableResources: [],
                        constraints: []
                      };
                      return {
                        ...prev,
                        content: {
                          ...gameContent,
                          investments: updatedInvestments
                        }
                      };
                    });
                  }}
                  onSelectedInvestmentIdsChange={(selectedIds) => {
                    setFormData(prev => {
                      const gameContent = (prev.content as GameScenarioContent) || {
                        objectives: [],
                        successCriteria: [],
                        availableResources: [],
                        constraints: []
                      };
                      return {
                        ...prev,
                        content: {
                          ...gameContent,
                          selectedInvestmentIds: selectedIds
                        }
                      };
                    });
                  }}
                />
              </motion.div>
            )}

            {activeTab === 'incidents' && (
              <motion.div
                key="incidents"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
                className="p-6"
              >
                <IncidentTab
                  incidents={(formData.content as GameScenarioContent)?.incidents || []}
                  selectedIncidentIds={(formData.content as GameScenarioContent)?.selectedIncidentIds || []}
                  showSelectedIncidents={true}
                  onChange={(updatedIncidents) => {
                    const gameContent = formData.content as GameScenarioContent || {
                      objectives: [],
                      successCriteria: [],
                      availableResources: [],
                      constraints: []
                    };
                    const updatedContent = {
                      ...gameContent,
                      incidents: updatedIncidents
                    };
                    handleFieldChange('content', updatedContent);
                  }}
                  onSelectedIncidentIdsChange={(selectedIds) => {
                    setFormData(prev => {
                      const gameContent = (prev.content as GameScenarioContent) || {
                        objectives: [],
                        successCriteria: [],
                        availableResources: [],
                        constraints: []
                      };
                      return {
                        ...prev,
                        content: {
                          ...gameContent,
                          selectedIncidentIds: selectedIds
                        }
                      };
                    });
                  }}
                />
              </motion.div>
            )}

            {activeTab === 'synergies' && (
              <motion.div
                key="synergies"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
                className="p-6"
              >
                <SynergyEditor
                  synergies={(formData.content as GameScenarioContent)?.synergies || []}
                  selectedInvestmentIds={(formData.content as GameScenarioContent)?.selectedInvestmentIds || []}
                  selectedIncidentIds={(formData.content as GameScenarioContent)?.selectedIncidentIds || []}
                  onChange={(updatedSynergies) => {
                    const gameContent = (formData.content as GameScenarioContent) || {
                      objectives: [],
                      successCriteria: [],
                      availableResources: [],
                      constraints: []
                    };
                    const updatedContent = {
                      ...gameContent,
                      synergies: updatedSynergies
                    };
                    handleFieldChange('content', updatedContent);
                  }}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Form Actions */}
          <div className={`p-6 border-t ${themeClasses.header} flex justify-between items-center`}>
            <StandardButton
              onClick={onCancel}
              variant="ghost"
              size="large"
              disabled={isSubmitting}
            >
              Cancel
            </StandardButton>

            <StandardButton
              type="submit"
              variant="primary"
              size="large"
              isLoading={isSubmitting}
              disabled={isSubmitting}
              rightIcon={!isSubmitting && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                </svg>
              )}
            >
              {mode === 'create' ? 'Create Scenario' : 'Save Changes'}
            </StandardButton>
          </div>
        </div>

        {/* Global Form Errors */}
        {validation.errors.submit && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 p-4 bg-red-500/20 border border-red-500/50 rounded-lg"
          >
            <ValidationErrors errors={validation.errors.submit} />
          </motion.div>
        )}
      </form>
    </div>
  );
};

export default SimpleScenarioForm;